import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { cardStat, cardStatInteractive, textBody } from "@design-system";
import { Customer, Driver } from "../../models";
import { IOrder } from "../../types/order/IOrder";

interface OrderParticipantCardsProps {
  customer?: IOrder["customer"];
  driver?: Driver;
  driverFallback?: string;
  showVehicle?: boolean;
  vehicleRegistration?: string;
}

/** Shared customer, driver and optional vehicle cards for order detail views. */
export const OrderParticipantCards = ({
  customer,
  driver,
  driverFallback = "--",
  showVehicle = false,
  vehicleRegistration,
}: OrderParticipantCardsProps) => {
  const { t } = useTranslation("common");
  const navigate = useNavigate();
  const customerName =
    (typeof customer === "string"
      ? customer
      : Customer.getCustomerName(customer)) ||
    t("unknown_customer", "Ukjent kunde");
  const customerPath =
    customer && typeof customer === "object" && "name" in customer
      ? `/customers?customer=${encodeURIComponent(customerName.toLowerCase().replace(/\s+/g, "-"))}`
      : undefined;
  const cards = [
    {
      label: t("customer"),
      value: customerName,
      path: customerPath,
      ariaLabel: t("navigate_to_customer", "Gå til kunde"),
    },
    {
      label: t("driver"),
      value: Driver.getDriverName(driver) || driverFallback,
      path: driver?.email
        ? `/resources/drivers?driver=${encodeURIComponent(driver.email)}`
        : undefined,
      ariaLabel: t("navigate_to_driver", "Gå til sjåfør"),
    },
    ...(showVehicle
      ? [
          {
            label: t("vehicle"),
            value: vehicleRegistration || "--",
            path: "/resources/vehicles",
            ariaLabel: t("navigate_to_vehicle", "Gå til kjøretøy"),
          },
        ]
      : []),
  ];
  return (
    <div
      className="flex flex-col sm:flex-row gap-4 mt-6"
      role="region"
      aria-label={t("order_participants", "Ordredeltakere")}
    >
      {cards.map((card, index) => {
        const content = (
          <>
            <div className="text-sm font-medium text-text mb-1">
              {card.label}
            </div>
            <div className={`text-sm ${textBody} break-words`}>
              {card.value}
            </div>
          </>
        );
        return card.path ? (
          <button
            key={index}
            type="button"
            className={`flex-1 min-w-0 text-left ${cardStatInteractive}`}
            aria-label={card.ariaLabel}
            onClick={() => navigate(card.path!)}
          >
            {content}
          </button>
        ) : (
          <div key={index} className={`flex-1 min-w-0 ${cardStat}`}>
            {content}
          </div>
        );
      })}
    </div>
  );
};
